import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import { useLanguage } from '../i18n/LanguageContext';

/* Accessibility statement — required by the Welsh Government Digital
 * Service Standard and the UK Public Sector Bodies (Websites and
 * Mobile Applications) Accessibility Regulations 2018. This page is
 * bilingual: the English version renders when lang=en and the Welsh
 * version renders when lang=cy. Because this is chrome-level content
 * (not long-form survivor guidance) it is safe to publish in Welsh. */

function EnStatement() {
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  return (
    <>
      <p className="eyebrow">ACCESSIBILITY STATEMENT</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(26px, 4vw, 34px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        Accessibility on The Full Impact
      </h1>
      <p
        className="font-body mt-4"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        The Full Impact is committed to making its website usable by as many
        people as possible, including survivors of sexual violence and domestic abuse
        who use assistive technologies, have cognitive or sensory disabilities, or
        access the site during periods of high stress or dissociation.
      </p>

      <h2 className="tf-h2 mt-8">What standard we aim for</h2>
      <p className="tf-p">
        We aim to meet <strong>Web Content Accessibility Guidelines (WCAG) 2.2 at
        Level AA</strong> and the Welsh Government <strong>Digital Service
        Standard</strong>. The site is being built and reviewed against these
        standards; it is not yet formally certified.
      </p>

      <h2 className="tf-h2 mt-8">What we have done</h2>
      <ul className="tf-ul">
        <li>Semantic HTML landmarks — one <code>&lt;main&gt;</code> per page, a real navigation region, page and section headings in order.</li>
        <li>A skip-to-main-content link as the first tab stop on every page.</li>
        <li>Visible keyboard focus indicators on every interactive element.</li>
        <li>Text and interactive elements meet WCAG AA colour-contrast ratios.</li>
        <li>Touch targets are at least 44&times;44 CSS pixels.</li>
        <li>Layout reflows without loss of function at 400% zoom on a 320px-wide viewport.</li>
        <li>Accessibility settings panel — larger text, higher contrast, dyslexia-friendly font, reduced motion.</li>
        <li>The site honours the operating-system <em>prefers-reduced-motion</em> setting.</li>
        <li>No autoplay video or audio. No pop-ups.</li>
        <li>All non-decorative images have text alternatives.</li>
        <li>Every interactive element is reachable by keyboard alone. Modals return focus to their trigger on close and can be dismissed with Escape.</li>
        <li>An anonymous quick-exit button is available on every page and is keyboard-operable.</li>
        <li>Bilingual English/Welsh site chrome (menu, buttons, footer, this statement). Long-form survivor guidance is currently in English only — see below.</li>
      </ul>

      <h2 className="tf-h2 mt-8">What is not yet accessible</h2>
      <ul className="tf-ul">
        <li>
          <strong>Welsh long-form content.</strong> The site chrome is bilingual, but
          the detailed survivor guidance is currently English-only. We are choosing to
          wait for professional Welsh translation rather than publish machine
          translation on a trauma-informed service. Welsh-preferring users can access
          the English content, or reach Welsh helplines via <a className="tf-link" href="https://livefearfree.gov.wales" target="_blank" rel="noopener noreferrer">Live Fear Free</a>.
        </li>
        <li>
          <strong>PDF workbooks.</strong> The Guided Journal PDFs (Colour and Black &amp; White) are provided for print use and have not yet been individually accessibility-tagged. The same content is available on the Guided Journal page as fully accessible HTML.
        </li>
        <li>
          <strong>Automated screen-reader testing.</strong> The site has been developed to WCAG 2.2 AA guidance but has not yet been independently audited by an external accessibility organisation.
        </li>
      </ul>

      <h2 className="tf-h2 mt-8">If something is not accessible to you</h2>
      <p className="tf-p">
        Please tell us. We treat accessibility issues as urgent — this site exists so
        survivors can find what they need, and if it fails you it has failed. Contact
        us on Instagram
        {' '}
        <a
          className="tf-link"
          href="https://www.instagram.com/thefullimpactcampaign"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="a11y-contact-instagram"
        >
          <Instagram size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" focusable="false" /> @thefullimpactcampaign
        </a>{' '}
        and briefly describe: the page you were on, what you tried to do, what
        happened, and (if you can) your device, browser and any assistive technology.
        We aim to reply within a few working days. If you need the information right
        now, please call one of the specialist helplines on the {' '}
        <Link to="/app/support" className="tf-link">Support</Link> page.
      </p>

      <h2 className="tf-h2 mt-8">Enforcement</h2>
      <p className="tf-p">
        In England, Wales and Scotland, the Equality and Human Rights Commission
        (EHRC) enforces the Public Sector Bodies (Websites and Mobile Applications)
        (No. 2) Accessibility Regulations 2018. Although we are not a public-sector
        body, we hold ourselves to the same standard. If you are not satisfied with
        our response, you can contact the EHRC.
      </p>

      <h2 className="tf-h2 mt-8">Statement preparation</h2>
      <p className="tf-p">
        This statement was prepared on {today}. The site is reviewed against WCAG 2.2
        AA on an ongoing basis. We will update this statement each time we complete a
        substantive accessibility change.
      </p>
    </>
  );
}

function CyStatement() {
  const today = new Date().toLocaleDateString('cy-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  return (
    <>
      <p className="eyebrow">DATGANIAD HYGYRCHEDD</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(26px, 4vw, 34px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        Hygyrchedd ar The Full Impact
      </h1>
      <p
        className="font-body mt-4"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Mae Ymgyrch The Full Impact wedi ymrwymo i wneud ei gwefan yn hygyrch i
        gymaint o bobl ag y bo modd, yn cynnwys goroeswyr trais rhywiol a
        cham-drin domestig sy’n defnyddio technoleg gynorthwyol, sydd ag
        anableddau gwybyddol neu synhwyraidd, neu sy’n defnyddio’r safle yn ystod
        cyfnodau o straen uchel neu ddatgysylltiad.
      </p>

      <h2 className="tf-h2 mt-8">Y safon rydym yn anelu ati</h2>
      <p className="tf-p">
        Rydym yn anelu i fodloni <strong>Canllawiau Hygyrchedd Cynnwys Gwe (WCAG)
        2.2 ar Lefel AA</strong> a <strong>Safon Gwasanaeth Digidol</strong>{' '}
        Llywodraeth Cymru. Mae’r safle’n cael ei adeiladu a’i adolygu yn erbyn y
        safonau hyn; nid yw wedi’i ardystio’n ffurfiol eto.
      </p>

      <h2 className="tf-h2 mt-8">Beth rydym wedi’i wneud</h2>
      <ul className="tf-ul">
        <li>Tirnodau HTML semantig — un <code>&lt;main&gt;</code> fesul tudalen, rhanbarth llywio go iawn, penawdau mewn trefn.</li>
        <li>Dolen “neidio i’r prif gynnwys” fel y stop tab cyntaf ar bob tudalen.</li>
        <li>Dangosyddion ffocws bysellfwrdd gweladwy ar bob elfen ryngweithiol.</li>
        <li>Mae testun ac elfennau rhyngweithiol yn bodloni cymarebau cyferbyniad lliw WCAG AA.</li>
        <li>Mae targedau cyffwrdd o leiaf 44&times;44 picsel CSS.</li>
        <li>Mae’r cynllun yn ail-lifo heb golli swyddogaeth ar chwyddo 400% ar borth gweld 320px o led.</li>
        <li>Panel gosodiadau hygyrchedd — testun mwy, cyferbyniad uwch, ffont sy’n hawdd i bobl ddyslecsig ei darllen, llai o symud.</li>
        <li>Mae’r safle’n parchu gosodiad <em>prefers-reduced-motion</em> y system weithredu.</li>
        <li>Dim fideo nac awdio awtoblae. Dim naidlenni.</li>
        <li>Mae gan bob delwedd nad yw’n addurniadol destun amgen.</li>
        <li>Mae pob elfen ryngweithiol yn cyrraedd trwy fysellfwrdd yn unig. Mae modalau yn dychwelyd ffocws i’w sbardun ar gau ac yn gadael cau â’r bysell Escape.</li>
        <li>Botwm gadael cyflym dienw ar bob tudalen, y gellir ei weithredu â’r bysellfwrdd.</li>
        <li>Elfennau’r wefan yn ddwyieithog Saesneg/Cymraeg. Mae canllawiau manwl i oroeswyr yn Saesneg yn unig ar hyn o bryd — gweler isod.</li>
      </ul>

      <h2 className="tf-h2 mt-8">Beth sydd ddim yn hygyrch eto</h2>
      <ul className="tf-ul">
        <li>
          <strong>Cynnwys hir yn Gymraeg.</strong> Mae elfennau’r wefan yn ddwyieithog, ond mae’r canllawiau manwl i oroeswyr yn Saesneg yn unig ar hyn o bryd. Rydym yn dewis aros am gyfieithiad proffesiynol Cymraeg yn hytrach na chyhoeddi cyfieithiad peiriannol ar wasanaeth sy’n ystyriol o drawma. Gall defnyddwyr sy’n gwneud dewis o’r Gymraeg gyrraedd cynnwys Saesneg, neu gyrraedd llinellau cymorth Cymraeg trwy <a className="tf-link" href="https://livefearfree.gov.wales" target="_blank" rel="noopener noreferrer">Byw Heb Ofn</a>.
        </li>
        <li>
          <strong>Llyfrau gwaith PDF.</strong> Nid yw PDFs y Dyddiadur Arweiniol (Lliw a Du a Gwyn) wedi eu tagio’n unigol am hygyrchedd eto. Mae’r un cynnwys ar gael ar y dudalen Dyddiadur Arweiniol fel HTML hygyrch llawn.
        </li>
        <li>
          <strong>Profion darllenydd sgrin awtomatig.</strong> Datblygwyd y safle i ganllawiau WCAG 2.2 AA ond nid yw wedi cael ei archwilio’n annibynnol eto gan sefydliad hygyrchedd allanol.
        </li>
      </ul>

      <h2 className="tf-h2 mt-8">Os oes rhywbeth ddim yn hygyrch i chi</h2>
      <p className="tf-p">
        Dywedwch wrthym. Rydym yn trin materion hygyrchedd fel rhai brys — mae’r safle hwn yn bodoli fel bod goroeswyr yn gallu dod o hyd i’r hyn sydd ei angen arnyn nhw, ac os yw’n eich methu chi mae wedi methu. Cysylltwch â ni ar Instagram
        {' '}
        <a
          className="tf-link"
          href="https://www.instagram.com/thefullimpactcampaign"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Instagram size={13} style={{ display: 'inline', verticalAlign: 'middle' }} aria-hidden="true" focusable="false" /> @thefullimpactcampaign
        </a>{' '}
        gan ddisgrifio’n gryno: y dudalen roeddech chi arni, beth roeddech chi’n ceisio ei wneud, beth ddigwyddodd, a (os gallwch chi) eich dyfais, porwr ac unrhyw dechnoleg gynorthwyol. Rydym yn anelu at ateb o fewn ychydig ddyddiau gwaith. Os oes angen y wybodaeth arnoch chi nawr, ffoniwch un o’r llinellau cymorth arbenigol ar y dudalen {' '}
        <Link to="/app/support" className="tf-link">Cymorth</Link>.
      </p>

      <h2 className="tf-h2 mt-8">Gorfodi</h2>
      <p className="tf-p">
        Yng Nghymru, Lloegr a’r Alban, mae’r Comisiwn Cydraddoldeb a Hawliau Dynol yn gorfodi Rheoliadau Hygyrchedd Cyrff Sector Cyhoeddus (Gwefannau a Chymwysiadau Symudol) (Rhif 2) 2018. Er nad ydym yn gorff sector cyhoeddus, rydym yn cadw ein hunain at yr un safon. Os nad ydych chi’n fodlon â’n hymateb, gallwch chi gysylltu â’r Comisiwn.
      </p>

      <h2 className="tf-h2 mt-8">Paratoi’r datganiad</h2>
      <p className="tf-p">
        Paratowyd y datganiad hwn ar {today}. Adolygir y safle yn erbyn WCAG 2.2 AA yn barhaus. Byddwn yn diweddaru’r datganiad hwn bob tro y byddwn yn cwblhau newid hygyrchedd sylweddol.
      </p>
    </>
  );
}

export default function Accessibility() {
  const { isCy } = useLanguage();
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="a11y-screen">
      <SEO
        title={isCy ? 'Datganiad Hygyrchedd' : 'Accessibility statement'}
        description={
          isCy
            ? 'Datganiad hygyrchedd Ymgyrch The Full Impact — WCAG 2.2 AA a Safon Gwasanaeth Digidol Llywodraeth Cymru.'
            : "The Full Impact's accessibility statement — WCAG 2.2 AA and the Welsh Government Digital Service Standard."
        }
        path="/app/accessibility"
      />
      {isCy ? <CyStatement /> : <EnStatement />}

      <div className="mt-8">
        <PrintButton label={isCy ? 'Argraffu / Cadw’r dudalen hon' : 'Print / Save this page'} />
      </div>
    </div>
  );
}
